/**
 * SchedulingKpiCard - Card KPI principal de agendamentos
 * Exibe total, atrasados, agendados e concluídos com avatar e filtro de período
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial
 */
import React from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { cn } from '@/lib/utils';
import type { PeriodFilter, PeriodPreset } from '@/hooks/useCrmOverviewFilters';

/** Props do SchedulingKpiCard */
interface SchedulingKpiCardProps {
  /** Total de agendamentos */
  totalScheduled: number;
  /** Atrasados (pendentes com data passada) */
  overdueCount: number;
  /** Agendados (pendentes com data futura) */
  upcomingCount: number;
  /** Concluídos no período */
  completedCount: number;
  /** Porcentagem de conclusão */
  completionPercentage: number;
  /** Se está carregando */
  isLoading: boolean;
  /** URL do avatar do usuário */
  avatarUrl?: string | null;
  /** Nome do usuário */
  userName?: string | null;
  /** Filtro de período atual */
  periodFilter: PeriodFilter;
  /** Callback para mudança de preset */
  onPresetChange: (preset: PeriodPreset) => void;
  /** Callback para período customizado */
  onCustomChange: (startDate: Date, endDate: Date) => void;
}

/**
 * Anel de progresso SVG ao redor do avatar
 */
function ProgressRing({ progress, size = 80 }: { progress: number; size?: number }) {
  const radius = (size / 2) - 4;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg
      className="absolute -inset-1"
      width={size + 8}
      height={size + 8}
      viewBox={`0 0 ${size + 8} ${size + 8}`}
    >
      {/* Fundo do anel */}
      <circle
        cx={(size + 8) / 2}
        cy={(size + 8) / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        className="text-muted/30"
      />
      {/* Progresso */}
      <circle
        cx={(size + 8) / 2}
        cy={(size + 8) / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        className="text-emerald-500 transition-all duration-500 ease-out"
        style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
      />
    </svg>
  );
}

/**
 * Card KPI principal de agendamentos
 */
export function SchedulingKpiCard({
  totalScheduled,
  overdueCount,
  upcomingCount,
  completedCount,
  completionPercentage,
  isLoading,
  avatarUrl,
  userName,
  periodFilter,
  onPresetChange,
  onCustomChange,
}: SchedulingKpiCardProps) {
  // Estado de loading
  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <div className="h-14 bg-gradient-to-r from-primary/20 to-primary/5" />
        <CardContent className="-mt-8 flex flex-col items-center pb-4">
          <Skeleton className="h-20 w-20 rounded-full" />
          <Skeleton className="h-4 w-28 mt-3" />
          <Skeleton className="h-6 w-24 mt-2 rounded-full" />
          <Skeleton className="h-8 w-full mt-3" />
          <Skeleton className="h-16 w-full mt-3 rounded-lg" />
          <div className="mt-3 w-full grid grid-cols-3 gap-2">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      {/* Header gradiente */}
      <div className="h-14 bg-gradient-to-r from-primary/20 to-primary/5" />

      <CardContent className="-mt-8 flex flex-col items-center pb-4">
        {/* Avatar com anel de progresso */}
        <div className="relative">
          <ProgressRing progress={completionPercentage} size={80} />
          <SmartAvatar
            src={avatarUrl}
            name={userName}
            size="2xl"
            className="border-4 border-background shadow-lg"
            use3DFallback={true}
          />
        </div>

        {/* Nome do usuário */}
        {userName && (
          <h3 className="mt-2 text-sm font-semibold text-foreground truncate max-w-full">
            {userName}
          </h3>
        )}

        {/* Badge de conclusão */}
        <Badge variant="secondary" className="mt-1 text-xs">
          {completionPercentage.toFixed(0)}% concluído
        </Badge>

        {/* Mini-filtro de período */}
        <div className="mt-3 w-full">
          <CrmPeriodFilter
            value={periodFilter}
            onPresetChange={onPresetChange}
            onCustomChange={onCustomChange}
            className="w-full h-8 text-xs"
          />
        </div>

        {/* Card Total centralizado */}
        <div className="mt-3 w-full text-center p-3 rounded-lg bg-muted/50">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
            Total Agendamentos
          </p>
          <p className="text-3xl font-bold text-foreground">
            {totalScheduled}
          </p>
        </div>

        {/* Grid 3 colunas: Atrasados, Agendados, Concluídos */}
        <div className="mt-3 w-full grid grid-cols-3 gap-2">
          {/* Atrasados */}
          <div className="text-center p-2 rounded-lg bg-destructive/10">
            <AlertTriangle className="h-4 w-4 mx-auto text-destructive mb-0.5" />
            <p className="text-[10px] font-medium text-muted-foreground">Atrasados</p>
            <p className={cn(
              "text-lg font-bold",
              overdueCount > 0 ? "text-destructive" : "text-foreground"
            )}>
              {overdueCount}
            </p>
          </div>

          {/* Agendados */}
          <div className="text-center p-2 rounded-lg bg-primary/10">
            <CalendarClock className="h-4 w-4 mx-auto text-primary mb-0.5" />
            <p className="text-[10px] font-medium text-muted-foreground">Agendados</p>
            <p className="text-lg font-bold text-primary">
              {upcomingCount}
            </p>
          </div>

          {/* Concluídos */}
          <div className="text-center p-2 rounded-lg bg-emerald-500/10">
            <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-600 dark:text-emerald-400 mb-0.5" />
            <p className="text-[10px] font-medium text-muted-foreground">Concluídos</p>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
              {completedCount}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
